import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { useAuth } from '@/auth/AuthProvider';
import { useAgency, writeStoredAgency } from '@/auth/AgencyProvider';
import { callRpc, useMutation } from '@/data/rpc';
import { useBillingFlags } from '@/data/useBilling';
import { captchaUrl, turnstileSiteKey } from '@/lib/config';
import { zoneCity } from '@/lib/dates';
import { loadTurnstile, TURNSTILE_ACTION, verifyCaptcha, type TurnstileApi } from '@/lib/turnstile';

/**
 * agency-billing F5 (design §9, T4.4): "Create my agency" on the no-agency screen. Built OFF: it renders
 * only while `billing_public_flags().self_signup_enabled` is on (a failed read counts as off). The server
 * is the gate (verified email, one self-created agency per user, the switch itself); this is the form.
 * On success the new agency becomes the stored choice and the membership read runs again, so the
 * layout opens on it with a trialing account.
 *
 * self-signup (design section 4): the form opens directly (the no-agency screen only mounts it when there
 * is no pending invite) and carries a Turnstile challenge. Submit hands the token to coreway-billing
 * (`POST /billing/captcha`), then calls the RPC with the same token, which consumes the single-use pass.
 * Any failure resets the widget: a token is good for one verification only. No site key configured: the
 * form says sign-up is unavailable and never submits.
 */
export const SELF_CREATE_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
] as const;

export function SelfCreateAgency() {
  const { t } = useTranslation();
  const flags = useBillingFlags();
  const { client } = useAuth();
  const agency = useAgency();
  const mutation = useMutation();
  const [open, setOpen] = React.useState(true);
  const [name, setName] = React.useState('');
  const [zone, setZone] = React.useState<string>('America/Chicago');
  const [token, setToken] = React.useState<string | null>(null);
  const widget = React.useRef<TurnstileHandle>(null);
  const siteKey = turnstileSiteKey();

  if (!flags.selfSignup) return null;
  if (!siteKey) {
    return (
      <div className="flex flex-col items-center gap-2 border-t border-hairline pt-4">
        <p className="max-w-prose text-base text-ink-secondary">{t('agency.selfCreate.unavailable')}</p>
      </div>
    );
  }
  if (!open) {
    return (
      <div className="flex flex-col items-center gap-2 border-t border-hairline pt-4">
        <p className="max-w-prose text-base text-ink-secondary">{t('agency.selfCreate.intro')}</p>
        <Button type="button" onClick={() => setOpen(true)}>
          {t('agency.selfCreate.cta')}
        </Button>
      </div>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    const res = await mutation.run(async () => {
      const check = await verifyCaptcha(captchaUrl(), token);
      if (check.error) return { data: null, error: check.error };
      return callRpc<string>(client, 'agency_self_create', { p_name: name.trim(), p_time_zone: zone, p_captcha_token: token });
    });
    if (!res.error && typeof res.data === 'string') {
      writeStoredAgency(res.data);
      if ('reload' in agency) agency.reload();
      return;
    }
    // The token is spent (or refused) either way: a fresh challenge for the next try.
    setToken(null);
    widget.current?.reset();
  };

  return (
    <form onSubmit={submit} className="flex w-full flex-col gap-4 border-t border-hairline pt-4 text-left">
      <p className="text-base text-ink-secondary">{t('agency.selfCreate.intro')}</p>
      <div className="flex flex-col gap-2">
        <Label htmlFor="self-create-name">{t('agency.selfCreate.name')}</Label>
        <Input id="self-create-name" value={name} maxLength={120} required onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="self-create-zone">{t('agency.selfCreate.timeZone')}</Label>
        <NativeSelect id="self-create-zone" value={zone} aria-describedby="self-create-zone-help" onChange={(e) => setZone(e.target.value)}>
          {SELF_CREATE_ZONES.map((z) => (
            <option key={z} value={z}>
              {t('agency.selfCreate.zone', { city: zoneCity(z) })}
            </option>
          ))}
        </NativeSelect>
        <p id="self-create-zone-help" className="text-sm text-ink-secondary">
          {t('agency.selfCreate.timeZoneHelp')}
        </p>
      </div>
      <div role="group" aria-labelledby="self-create-check" aria-describedby="self-create-check-help" className="flex flex-col gap-2">
        <span id="self-create-check" className="text-sm font-medium text-ink">
          {t('agency.selfCreate.check')}
        </span>
        <TurnstileWidget ref={widget} siteKey={siteKey} onToken={setToken} />
        <p id="self-create-check-help" className="text-sm text-ink-secondary">
          {t('agency.selfCreate.checkHelp')}
        </p>
      </div>
      {mutation.error && (
        <p role="alert" className="text-base text-danger">
          {mutation.error}
        </p>
      )}
      <div className="flex gap-3">
        <Button type="submit" disabled={mutation.busy || name.trim() === '' || !token}>
          {mutation.busy ? t('agency.selfCreate.creating') : t('agency.selfCreate.submit')}
        </Button>
        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}

interface TurnstileHandle {
  reset(): void;
}

/**
 * The challenge, rendered explicitly into a ref'd div (never `id="turnstile"`, which would shadow the
 * `window.turnstile` API). `onToken(null)` whenever the token expires or the widget errors.
 */
const TurnstileWidget = React.forwardRef<TurnstileHandle, { siteKey: string; onToken(token: string | null): void }>(
  function TurnstileWidget({ siteKey, onToken }, ref) {
    const { t, i18n } = useTranslation();
    const container = React.useRef<HTMLDivElement>(null);
    const api = React.useRef<TurnstileApi | null>(null);
    const widgetId = React.useRef<string | undefined>(undefined);
    const tokenCb = React.useRef(onToken);
    tokenCb.current = onToken;
    const [loadError, setLoadError] = React.useState(false);
    const language = i18n.resolvedLanguage === 'es' ? 'es' : 'en';

    React.useImperativeHandle(ref, () => ({
      reset() {
        if (api.current && widgetId.current !== undefined) api.current.reset(widgetId.current);
      },
    }));

    React.useEffect(() => {
      let active = true;
      loadTurnstile().then(
        (turnstile) => {
          if (!active || !container.current) return;
          api.current = turnstile;
          widgetId.current = turnstile.render(container.current, {
            sitekey: siteKey,
            action: TURNSTILE_ACTION,
            language,
            callback: (value) => tokenCb.current(value),
            'expired-callback': () => tokenCb.current(null),
            'error-callback': () => tokenCb.current(null),
          });
        },
        (err: unknown) => {
          console.warn('turnstile did not load', err);
          if (active) setLoadError(true);
        },
      );
      return () => {
        active = false;
        if (api.current && widgetId.current !== undefined) api.current.remove(widgetId.current);
        widgetId.current = undefined;
        tokenCb.current(null);
      };
    }, [siteKey, language]);

    return (
      <>
        <div ref={container} data-testid="turnstile-widget" className="min-h-16" />
        {loadError && (
          <p role="alert" className="text-sm text-danger">
            {t('agency.selfCreate.checkLoadError')}
          </p>
        )}
      </>
    );
  },
);
